// src/pages/SubmitOrderPage.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "../services/mysqlStore";
import { ref, uploadBytes, getDownloadURL, storage } from "../services/mysqlStorage";
import { db } from "../services/mysqlStore";
import "./SubmitOrderPage.css";
import { useSocialDetails } from "../hooks/useSocialDetails";

// Same EmailJS service/template already used across the admin panel
// (AdminMessages, AdminSendOffers) — reused here to notify the admin
// whenever a new custom order is submitted.
const EMAILJS_SERVICE_ID  = "service_fcawjbv";
const EMAILJS_TEMPLATE_ID = "template_fn9pni5";
const EMAILJS_PUBLIC_KEY  = "dnkXQFz4IKdDFDJmu";

async function notifyAdminOfNewOrder(adminEmail, order) {
  if (!adminEmail) {
    // This is almost certainly why no email arrives: sd.email came back
    // empty/undefined from Firestore (siteSettings/socialDetails), so we
    // never even attempt to send. Check that document's "email" field.
    console.warn(
      "[Admin notify] Skipped — no admin email found in siteSettings/socialDetails. " +
      "Go to Admin Panel > Social Details and make sure the Email Address field is filled in and saved."
    );
    return;
  }

  const bodyLines = [
    `A new custom order has been submitted on the website.`,
    ``,
    `Company: ${order.companyName}`,
    `Email: ${order.email}`,
    `Phone: ${order.phone}`,
    `Quote Number: ${order.quoteNumber}`,
    `Qty Required: ${order.qtyRequired}`,
    `PO: ${order.po || "-"}`,
    `Expected Date: ${order.expectedDate}`,
    `Shipping: ${order.shippingOption === "ship" ? "Ship" : "Pickup"}`,
    `Notes: ${order.notes || "-"}`,
    ``,
    `Please check the Admin Panel > Orders section for full details and files.`,
  ];

  console.log("[Admin notify] Sending order notification to:", adminEmail);

  const response = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      service_id:  EMAILJS_SERVICE_ID,
      template_id: EMAILJS_TEMPLATE_ID,
      user_id:     EMAILJS_PUBLIC_KEY,
      template_params: {
        to_email:      adminEmail,
        subject:       `New Custom Order Submitted — ${order.companyName}`,
        reply_message: bodyLines.join("\n"),
        customer_name: "Admin",
      }
    })
  });

  const responseText = await response.text();
  if (!response.ok) {
    // EmailJS puts the real reason (bad template, invalid recipient,
    // quota exceeded, etc.) in the response body — log it so it's visible.
    console.error("[Admin notify] EmailJS rejected the request:", response.status, responseText);
    throw new Error("EmailJS send failed: " + responseText);
  }

  console.log("[Admin notify] EmailJS accepted the request:", responseText);
}

const FEES = [
  { item: "Epson", fee: "£30", unit: "per Epson (1 side)", comment: "e.g. 1–16pg signatures. Perfecting (2 sides) = £60." },
  { item: "Dylux", fee: "£30", unit: "per Dylux (2 sides)", comment: "" },
  { item: "File retrieval", fee: "£30", unit: "per hour", comment: "" },
  { item: "File work", fee: "£30", unit: "per hour", comment: "" },
  { item: "Native file to PDF", fee: "£30", unit: "for the 1st hour", comment: "extra £30 per extra hour" },
  { item: "Submitting new file", fee: "£20", unit: "1 File Change", comment: "" },
];

export default function SubmitOrderPage() {
  const navigate = useNavigate();
  const { data: sd } = useSocialDetails();
  const [activeTab, setActiveTab] = useState("form");

  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [qty, setQty] = useState("");
  const [po, setPo] = useState("");
  const [notes, setNotes] = useState("");
  const [expectedDate, setExpectedDate] = useState("");

  const [orderFile, setOrderFile] = useState(null);
  const [artworkFile, setArtworkFile] = useState(null);

  const [shippingOption, setShippingOption] = useState("pickup"); // 'pickup' | 'ship'
  const [contactName, setContactName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Canada");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeFees, setAgreeFees] = useState(false);
  const [showFeesModal, setShowFeesModal] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function handleFileChange(setter) {
    return function (e) {
      setter(e.target.files?.[0] || null);
    };
  }

  async function uploadIfPresent(file, folder) {
    if (!file) return null;
    const safeName = file.name.replace(/\s+/g, "_");
    const fileRef = ref(storage, `${folder}/${Date.now()}_${safeName}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!companyName || !email || !phone || !quoteNumber || !qty || !expectedDate) {
      setError("Please fill in all required fields marked with *.");
      return;
    }
    if (shippingOption === "ship" && (!contactName || !address1 || !city || !country || !province || !postalCode)) {
      setError("Please complete the shipping address fields.");
      return;
    }
    if (!agreeTerms || !agreeFees) {
      setError("Please agree to the Terms of Service and the Additional Fees Schedule before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const [orderFileUrl, artworkFileUrl] = await Promise.all([
        uploadIfPresent(orderFile, "custom-orders/files"),
        uploadIfPresent(artworkFile, "custom-orders/artwork"),
      ]);

      const orderData = {
        companyName, email, phone, quoteNumber,
        qtyRequired: qty, po, notes, expectedDate,
        orderFileUrl, artworkFileUrl,
        shippingOption,
        shippingAddress: shippingOption === "ship" ? {
          contactName, address1, address2, city, country, province, postalCode,
        } : null,
        agreeTerms, agreeFees,
        status: "submitted",
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "customOrders"), orderData);

      // Fire-and-forget: notify admin by email. Order is already saved in
      // Firestore, so if this fails the order isn't lost — it just won't
      // trigger an email, and will still show up in the Admin Panel.
      notifyAdminOfNewOrder(sd.email, orderData).catch((err) => {
        console.error("Admin notification email failed:", err);
      });

      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong while submitting your order. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="submit-order-page">
        <div className="container">
          <div className="order-success anim-pop-in">
            <h2>Order Submitted Successfully!</h2>
            <p>Thank you for choosing Circuit Printing Solutions. Our team will review your request and begin processing your order shortly. We'll be in touch via email if anything else is needed.</p>
            <Link to="/" className="btn-primary">Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-order-page">
      <div className="submit-order-hero">
        <div className="container anim-fade-in-up">
          <span className="tag">Custom Order</span>
          <h1 className="section-title">Submit Custom Order</h1>
          <p className="section-subtitle">
            Thank you for choosing Circuit Printing Solutions for your custom printing needs.
            We are excited to move forward with your project! To ensure a smooth and efficient
            ordering process, make sure that:
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="container" style={{ paddingTop: 28 }}>
        <div className="so-tabs">
          <button className={"so-tab" + (activeTab === "form" ? " active" : "")} onClick={() => setActiveTab("form")}>
            📝 Submit Order Form
          </button>
          <button className={"so-tab" + (activeTab === "contact" ? " active" : "")} onClick={() => setActiveTab("contact")}>
            📞 Contact Us Directly
          </button>
        </div>
      </div>

      {activeTab === "contact" && (
        <div className="container so-contact-wrap">
          <div className="so-contact-card">
            <div className="so-contact-icon">💬</div>
            <h2 className="so-contact-title">Place Your Order Directly</h2>
            <p className="so-contact-desc">
              Prefer to speak with us? Reach out via WhatsApp or give us a call — we will take your order details and get you a quote right away.
            </p>
            <div className="so-contact-buttons">
              {(sd.whatsapp || sd.phone) && (
                <a
                  href={"https://wa.me/" + (sd.whatsapp || sd.phone || "").replace(/[^0-9]/g, "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="so-contact-btn so-whatsapp-btn"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp Us
                </a>
              )}
              {sd.phone && (
                <a
                  href={"tel:" + (sd.phone || "").replace(/\s/g, "")}
                  className="so-contact-btn so-call-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.69 12 19.79 19.79 0 011.61 3.41 2 2 0 013.6 1.21h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.91 8.8a16 16 0 006.29 6.29l.95-.95a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  Call Us: {sd.phone}
                </a>
              )}
            </div>
            <div className="so-contact-note">
              <strong>Business Hours:</strong> Mon–Fri, 9am–6pm EST
            </div>
          </div>
        </div>
      )}

      {activeTab === "form" && (
      <>
      <div className="container submit-order-layout" style={{ marginTop: 32, marginBottom: 60 }}>
        <ul className="checklist reveal-left">
          <li><CheckIcon /> You have been provided with a quote number</li>
          <li><CheckIcon /> Your quote matches your order request</li>
          <li><CheckIcon /> The shipping selection is the same as the quote</li>
          <li><CheckIcon /> Your file is print ready</li>
        </ul>

        <form className="submit-order-form reveal-right" onSubmit={handleSubmit}>
          <h2>Submit Order</h2>

          {error && <div className="form-error">{error}</div>}

          <div className="form-row">
            <label>Company Name *</label>
            <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
          </div>

          <div className="form-row-group">
            <div className="form-row">
              <label>Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="form-row">
              <label>Phone *</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
            </div>
          </div>

          <div className="form-row">
            <label>Quote Number *</label>
            <input value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} required />
            <span className="field-hint">
              If you do not have a quote number, please <Link to="/contact">request one here</Link>.
            </span>
          </div>

          <div className="form-row-group">
            <div className="form-row">
              <label>Qty Required *</label>
              <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} required />
              <span className="field-hint">For special quantity instructions, use Additional Notes below.</span>
            </div>
            <div className="form-row">
              <label>PO</label>
              <input value={po} onChange={(e) => setPo(e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <label>Additional Notes</label>
            <textarea rows="4" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Please include any specific production instructions here so they can be added to the docket." />
          </div>

          <div className="form-row">
            <label>Expected Date *</label>
            <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} required />
            <span className="field-hint">
              Estimated turnaround for custom jobs can range from 5 to 10+ business days from artwork approval,
              depending on pre-press validation, bindery needs and current workflow volume. Contact us to confirm
              specific timing for your order.
            </span>
          </div>

          <div className="form-row-group">
            <div className="form-row">
              <label>Upload File <span className="field-hint-inline">(Max 1000 MB)</span></label>
              <input type="file" onChange={handleFileChange(setOrderFile)} />
              <span className="field-hint">Please ensure filenames do not contain spaces or special characters.</span>
            </div>
            <div className="form-row">
              <label>Upload Artwork</label>
              <input type="file" onChange={handleFileChange(setArtworkFile)} />
            </div>
          </div>

          <div className="form-divider" />

          <h3>Shipping</h3>

          <div className="shipping-options">
            <label className={`shipping-option ${shippingOption === "pickup" ? "active" : ""}`}>
              <input type="radio" name="shipping" value="pickup"
                checked={shippingOption === "pickup"}
                onChange={() => setShippingOption("pickup")} />
              Pickup at Circuit Printing Facility
            </label>
            <label className={`shipping-option ${shippingOption === "ship" ? "active" : ""}`}>
              <input type="radio" name="shipping" value="ship"
                checked={shippingOption === "ship"}
                onChange={() => setShippingOption("ship")} />
              Ship for me
            </label>
          </div>

          {shippingOption === "ship" && (
            <div className="shipping-fields anim-fade-in-up">
              <div className="form-row">
                <label>Contact / Business Name *</label>
                <input value={contactName} onChange={(e) => setContactName(e.target.value)} required />
              </div>
              <div className="form-row-group">
                <div className="form-row">
                  <label>Address Line 1 *</label>
                  <input value={address1} onChange={(e) => setAddress1(e.target.value)} required />
                </div>
                <div className="form-row">
                  <label>Address Line 2</label>
                  <input value={address2} onChange={(e) => setAddress2(e.target.value)} />
                </div>
              </div>
              <div className="form-row-group">
                <div className="form-row">
                  <label>City *</label>
                  <input value={city} onChange={(e) => setCity(e.target.value)} required />
                </div>
                <div className="form-row">
                  <label>Country *</label>
                  <select value={country} onChange={(e) => setCountry(e.target.value)} required>
                    <option>Canada</option>
                    <option>Ireland</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row-group">
                <div className="form-row">
                  <label>County / State *</label>
                  <input value={province} onChange={(e) => setProvince(e.target.value)} required />
                </div>
                <div className="form-row">
                  <label>Postcode / Zip Code *</label>
                  <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required />
                </div>
              </div>
              <p className="shipping-note">Free delivery offers cannot be applied to custom orders.</p>
            </div>
          )}

          <div className="form-divider" />

          <label className="checkbox-row">
            <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} />
            <span>I have read and I agree to the <Link to="/privacy-policy">Terms of Service</Link> of this website.</span>
          </label>

          <label className="checkbox-row">
            <input type="checkbox" checked={agreeFees} onChange={(e) => setAgreeFees(e.target.checked)} />
            <span>
              I have read the{" "}
              <button type="button" className="link-button" onClick={() => setShowFeesModal(true)}>
                Additional Fees Schedule
              </button>{" "}
              and I agree to pay for any additional costs related to extra proofing or file modifications
              resulting from changes required to my files.
            </span>
          </label>

          <button type="submit" className="btn-primary submit-order-btn" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Order →"}
          </button>
        </form>
      </div>

      {showFeesModal && (
        <div className="fees-modal-overlay" onClick={() => setShowFeesModal(false)}>
          <div className="fees-modal anim-pop-in" onClick={(e) => e.stopPropagation()}>
            <button className="fees-modal-close" onClick={() => setShowFeesModal(false)}>×</button>
            <h3>Additional Fees Schedule</h3>
            <p className="fees-modal-note">
              Hard copy Epsons and Dylux will be made at the time your low-res PDF proof is emailed to you.
              Should changes be required, please see the additional charges below:
            </p>
            <table className="fees-table">
              <thead>
                <tr><th>Item</th><th>Fee</th><th>Unit</th><th>Comments</th></tr>
              </thead>
              <tbody>
                {FEES.map((f) => (
                  <tr key={f.item}>
                    <td>{f.item}</td>
                    <td>{f.fee}</td>
                    <td>{f.unit}</td>
                    <td>{f.comment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}